/**
 * CollectionListControl — Multi-select collection picker with drag-reorder and image overrides.
 *
 * Stores value as a JSON string matching the storefront `data-category-list` contract:
 * [{ handle, title, image, linkUrl, mode }]
 */

import { useState, useMemo, useCallback, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2, ImageIcon } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  useCollections,
  useStorefrontTenantKey,
} from '@/features/storefront/collections/hooks'
import { cn } from '@/lib/utils'
import { uploadFile } from '@/lib/erp/files'

// ---------------------------------------------------------------------------
// Types — matches storefront data-category-list contract
// ---------------------------------------------------------------------------

interface CollectionEntry {
  handle: string
  title: string
  image: string | null
  linkUrl: string | null
  mode: string
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CollectionListControlProps {
  value: string
  onChange: (v: string) => void
  label: string
}

// ---------------------------------------------------------------------------
// Sortable Row
// ---------------------------------------------------------------------------

interface SortableRowProps {
  entry: CollectionEntry
  onRemove: () => void
  onImageOverride: () => void
  isUploading?: boolean
}

function SortableRow({ entry, onRemove, onImageOverride, isUploading }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.handle })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-sm border border-gray-200 bg-white px-2 py-1.5',
        isDragging && 'z-10 shadow-md opacity-90',
      )}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab text-gray-400 hover:text-gray-600 touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Thumbnail */}
      <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md bg-gray-100">
        {isUploading ? (
          <div className="flex h-full w-full items-center justify-center">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          </div>
        ) : entry.image ? (
          <img src={entry.image} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ImageIcon className="h-3.5 w-3.5 text-gray-300" />
          </div>
        )}
      </div>

      {/* Title */}
      <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
        {entry.title}
      </span>

      {/* Image override */}
      <button
        type="button"
        onClick={onImageOverride}
        title="Override image"
        className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <ImageIcon className="h-3.5 w-3.5" />
      </button>

      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        title="Remove"
        className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function CollectionListControl({ value, onChange, label }: CollectionListControlProps) {
  const [addOpen, setAddOpen] = useState(false)
  const [search, setSearch] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [overrideTarget, setOverrideTarget] = useState<string | null>(null)

  // Parse stored JSON
  const entries: CollectionEntry[] = useMemo(() => {
    try {
      const parsed = JSON.parse(value || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [value])

  // Fetch available collections
  const { tenantKey } = useStorefrontTenantKey()
  const { data: collections, isLoading } = useCollections(tenantKey)

  // Filter out already-added collections
  const addedSlugs = useMemo(() => new Set(entries.map((e) => e.handle)), [entries])
  const availableCollections = useMemo(
    () =>
      (collections ?? []).filter(
        (col) => !addedSlugs.has(col.slug) && col.is_visible,
      ),
    [collections, addedSlugs],
  )

  // Filtered by search
  const filteredCollections = useMemo(() => {
    if (!search) return availableCollections
    const q = search.toLowerCase()
    return availableCollections.filter((col) => col.title.toLowerCase().includes(q))
  }, [availableCollections, search])

  // Persist entries as JSON string
  const persist = useCallback(
    (next: CollectionEntry[]) => onChange(JSON.stringify(next)),
    [onChange],
  )

  // Add a collection
  const handleAdd = useCallback(
    (slug: string) => {
      const col = (collections ?? []).find((c) => c.slug === slug)
      if (!col) return
      const entry: CollectionEntry = {
        handle: col.slug,
        title: col.title,
        image: col.banner_image,
        linkUrl: `/collections/${col.slug}`,
        mode: 'collection',
      }
      persist([...entries, entry])
      setAddOpen(false)
      setSearch('')
    },
    [collections, entries, persist],
  )

  // Remove a collection
  const handleRemove = useCallback(
    (handle: string) => persist(entries.filter((e) => e.handle !== handle)),
    [entries, persist],
  )

  // Drag-reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = entries.findIndex((e) => e.handle === active.id)
      const newIndex = entries.findIndex((e) => e.handle === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      persist(arrayMove(entries, oldIndex, newIndex))
    },
    [entries, persist],
  )

  // Image override via file input
  const handleImageOverride = useCallback((handle: string) => {
    setOverrideTarget(handle)
    fileInputRef.current?.click()
  }, [])

  const [uploading, setUploading] = useState<string | null>(null)

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !overrideTarget) return
      // Reset file input so same file can be re-selected
      e.target.value = ''

      setUploading(overrideTarget)
      try {
        const result = await uploadFile(file, { isPrivate: false })
        persist(
          entries.map((entry) =>
            entry.handle === overrideTarget ? { ...entry, image: result.file_url } : entry,
          ),
        )
      } catch (err) {
        console.error('[CollectionListControl] Image upload failed:', err)
      } finally {
        setUploading(null)
        setOverrideTarget(null)
      }
    },
    [entries, overrideTarget, persist],
  )

  return (
    <div className="space-y-2">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>

      {/* Hidden file input for image overrides */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Sortable list */}
      {entries.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={entries.map((e) => e.handle)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {entries.map((entry) => (
                <SortableRow
                  key={entry.handle}
                  entry={entry}
                  onRemove={() => handleRemove(entry.handle)}
                  onImageOverride={() => handleImageOverride(entry.handle)}
                  isUploading={uploading === entry.handle}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add collection button + popover */}
      <Popover open={addOpen} onOpenChange={setAddOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-center gap-1.5 rounded-sm border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700"
          >
            <Plus className="h-4 w-4" />
            Add Collection
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search collections..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading && (
                <div className="p-3 text-center text-sm text-gray-400">Loading...</div>
              )}
              {!isLoading && filteredCollections.length === 0 && (
                <CommandEmpty>
                  {availableCollections.length === 0
                    ? 'All collections added'
                    : 'No matching collections'}
                </CommandEmpty>
              )}
              {filteredCollections.length > 0 && (
                <CommandGroup>
                  {filteredCollections.map((col) => (
                    <CommandItem
                      key={col.slug}
                      value={col.slug}
                      onSelect={() => handleAdd(col.slug)}
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 shrink-0 overflow-hidden rounded-md bg-gray-100">
                          {col.banner_image ? (
                            <img
                              src={col.banner_image}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <ImageIcon className="h-3 w-3 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <span className="truncate">{col.title}</span>
                        <span className="ml-auto shrink-0 text-xs text-gray-400">
                          {col.product_count}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
